import * as React from 'react';
import {BrowserRouter, Route, Switch} from 'react-router-dom';
import DashboardLoaderContainer from '../DashboardLoader/DashboardLoaderContainer';
import ReviewLoaderContainer from '../ReviewLoader/ReviewLoaderContainer';
import TermsOfServicePrompt from '../TermsOfService/TermsOfServicePrompt';
import TermsOfService from '../TermsOfService/TermsOfService';
import WebConnector from '../../services/WebConnector/WebConnector';
import FeedbackUserService from '../../services/FeedbackUserService/FeedbackUserService';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Config from '../../globals/Config';
import PreferencesContainer from '../Preferences/PreferencesContainer';
import Strings from '../../globals/Strings';
import Constants, {ModalStates, AnnotationModes} from '../../globals/Constants';
import AuthorizationModel from '../../types/AuthorizationsModel';
import SkeletonPage from '../SkeletonPage/SkeletonPage';
import ClientSideError from '../ClientSideError/ClientSideError';
import Themeable from '../../types/Themeable';
import {MyWindow} from '../../types/MyWindow';
import ModalManagerContainer from '../ModalManager/ModalManagerContainer';
import SmartPlayerService from '../../services/SmartPlayerService/SmartPlayerService';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import AuthenticationAwareRouteContainer, {OnLoginCompleteRouteCallback} from '../AuthenticationAwareRoute/AuthenticationAwareRouteContainer';

declare const window: MyWindow;

const updateQueryStringAndReplaceHistory = (queryStringKey: string, queryStringValue: string, browserHistory: any, location: any) => {
   const updatedParams = new URLSearchParams(location.search);
   if (queryStringValue) {
      updatedParams.set(queryStringKey, queryStringValue);
   } else {
      updatedParams.delete(queryStringKey);
   }
   location.search = updatedParams.toString();
   browserHistory.replace(location);
};

// tslint:disable-next-line: variable-name - Justification: Components should be title cased
const RouterWithSplash: React.FC<RouterWithSplashProps> = ({
   themeName,
   authorization,
   showPageLoadingSpinner,
   annotationMode,
   setAnnotationMode,
   setModalState
}) => {
   const [isLoggingIn, setIsLoggingIn] = React.useState(true);

   React.useEffect(() => {
      SmartPlayerService.injectSmartPlayer();
      GoogleAnalyticsService.initialize();

      FeedbackUserService.initializeFeedbackUser();

      if (Config.user.isAuthenticated) {
         WebConnector.loginAndSave().then(() => {
            setIsLoggingIn(false);
         });
      } else {
         setIsLoggingIn(false);
      }
   }, []);

   const onEnterShareReview: OnLoginCompleteRouteCallback<{reviewId: string}> = (routeHistory, _, match) => {
      if (authorization.authorizedActions && authorization.authorizedActions.canCreateReviews) {
         setModalState(ModalStates.shareReviewWithCreatedHeader);
      }

      routeHistory.replace(`${Constants.siteLinks.review}/${match.params.reviewId}`);
   };

   const onEnterNewReview: OnLoginCompleteRouteCallback<{reviewId: string}> = (routeHistory, location) => {
      if (authorization.authorizedActions && authorization.authorizedActions.canCreateReviews) {
         setModalState(ModalStates.newReview);
      }

      routeHistory.replace(`${Constants.siteLinks.dashboard}${location.search}`);
   };

   const onEnterReview: OnLoginCompleteRouteCallback<{reviewId: string, commentId?: string, versionNumber?: string}> = (routeHistory, location, match) => {
      let versionNumber: number = 1;

      if (match.params.versionNumber) {
         if (match.params.versionNumber.length < 5) {
            versionNumber = +match.params.versionNumber;
         } else {
            if (!match.params.commentId) {
               // i think this guards the _extreme_ corner case that your commentId starts with 'v' and is
               // all numbers after so it matches the wrong route (i.e. /review/someReviewId/v82853253 where v82853253 refers
               // to a comment, not a version)
               match.params.commentId = `v${match.params.versionNumber}`;  // hardcode the value including the 'v' back on the match.commentId for use in this func
            }
         }
      }

      const basePath: string = `/review/${match.params.reviewId}/v${versionNumber}`;

      if (basePath !== location.pathname) {
         location.pathname = basePath;
         updateQueryStringAndReplaceHistory('commentid', match.params.commentId, routeHistory, location);
      }

      if (annotationMode === AnnotationModes.edit) {
         setAnnotationMode(AnnotationModes.none);
      }
   };

   const themeNameClass = `${themeName}-theme`;

   return (
      <div className={themeNameClass}>
         {isLoggingIn && <SkeletonPage />}
         <LoadingSpinner visible={isLoggingIn || showPageLoadingSpinner} />
         <BrowserRouter>
            <Switch>
               <Route exact path="/review/:reviewId/share">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false} onLoginComplete={onEnterShareReview}>
                     <ReviewLoaderContainer />
                  </AuthenticationAwareRouteContainer>
               </Route>
               <Route exact path="/review/:reviewId/v:versionNumber(\d+)/:commentId?">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false} onLoginComplete={onEnterReview}>
                     <ReviewLoaderContainer />
                  </AuthenticationAwareRouteContainer>
               </Route>
               <Route exact path="/review/:reviewId/:commentId?">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false} onLoginComplete={onEnterReview}>
                     <ReviewLoaderContainer />
                  </AuthenticationAwareRouteContainer>
               </Route>
               <Route exact path="/termsofserviceprompt">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={true}>
                     <TermsOfServicePrompt />
                  </AuthenticationAwareRouteContainer>
               </Route>
               <Route exact path="/termsofservice">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false}>
                     <TermsOfService />
                  </AuthenticationAwareRouteContainer>
               </Route>
               <Route exact path="/preferences">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={true}>
                     <PreferencesContainer />
                  </AuthenticationAwareRouteContainer>
               </Route >
               <Route exact path="/newreview">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false} onLoginComplete={onEnterNewReview}>
                     <DashboardLoaderContainer />
                  </AuthenticationAwareRouteContainer>
               </Route >
               <Route exact path="/">
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={true}>
                     <DashboardLoaderContainer />
                  </AuthenticationAwareRouteContainer>
               </Route >
               {
                  Config.reviewRoutePrefix && <Route path={Config.reviewRoutePrefix + ':reviewId/'}>
                     <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false}>
                        <ReviewLoaderContainer />
                     </AuthenticationAwareRouteContainer>
                  </Route>
               }
               <Route>
                  <AuthenticationAwareRouteContainer isLoggingIn={isLoggingIn} isAuthRequired={false}>
                     <ClientSideError title={Strings.errors.pageError} text1={Strings.errors.pageErrorDetail} showHeaderButtons={true} statusCode={Constants.statusCodes.notFound} />
                  </AuthenticationAwareRouteContainer>
               </Route>
            </Switch>
            <ModalManagerContainer />
         </BrowserRouter>
      </div>
   );
};

export default RouterWithSplash;

export interface RouterWithSplashProps extends Themeable {
   authorization: AuthorizationModel;
   showPageLoadingSpinner: boolean;
   annotationMode: AnnotationModes;
   setAnnotationMode(annotationMode: AnnotationModes): void;
   setModalState(modalState: ModalStates): void;
}
